import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FlaskConical, Loader2, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function TestGenerator() {
  const navigate = useNavigate();
  const [regions, setRegions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [cfg, setCfg] = useState({
    count: 50, patients: 40, region: "autun",
    pct_sanitaire: 80, pct_prive: 15, pct_tourisme: 5,
    date: todayStr(), days: 1, time_start: "05:00", time_end: "18:00",
    cluster: true, auto_regul: true, city: "", radius_km: 15,
  });

  const journeeType = () => setCfg((c) => ({
    ...c, count: 120, days: 1, time_start: "06:00", time_end: "19:00",
    cluster: true, auto_regul: true, patients: 90,
  }));

  useEffect(() => {
    api.get("/company/test-data/regions").then((r) => setRegions(r.data.regions || [])).catch(() => {});
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const totalPct = Number(cfg.pct_sanitaire) + Number(cfg.pct_prive) + Number(cfg.pct_tourisme);

  const generate = async () => {
    setBusy(true);
    try {
      const body = {
        count: Number(cfg.count), patients: Number(cfg.patients) || null, region: cfg.region,
        pct_sanitaire: Number(cfg.pct_sanitaire), pct_prive: Number(cfg.pct_prive), pct_tourisme: Number(cfg.pct_tourisme),
        date: cfg.date, days: Number(cfg.days), time_start: cfg.time_start, time_end: cfg.time_end,
        cluster: !!cfg.cluster, auto_regul: !!cfg.auto_regul,
        city: cfg.city?.trim() || null, radius_km: Number(cfg.radius_km) || 15,
      };
      const { data } = await api.post("/company/test-data/generate", body);
      setLast(data);
      const grp = Object.values(data.regul || {}).reduce((a, r) => a + (r.grouped || 0), 0);
      const sur = Object.values(data.regul || {}).reduce((a, r) => a + (r.surcharge || 0), 0);
      toast.success(`${data.rides} courses générées sur ${data.dates.length} jour(s) · ${data.patients} patients${cfg.auto_regul ? ` · ${grp} mutualisée(s) · ${sur} en surcharge` : ""}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const clearAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/company/test-data/clear");
      setLast(null);
      toast.success(`Supprimé : ${data.rides} courses · ${data.patients} patients de test`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="test-generator-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><FlaskConical className="h-6 w-6 text-primary" /> Générateur de test</h1>
        <p className="text-muted-foreground text-sm">Créez des jeux de courses réalistes pour tester la régulation, la mutualisation et faire des démonstrations. Ces données sont marquées « test » et supprimables en un clic.</p>
      </div>

      <Card className="p-6 rounded-md shadow-none space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Nombre de courses (par jour)</Label>
            <Input type="number" min="1" max="500" value={cfg.count} onChange={(e) => set("count", e.target.value)} className="mt-1" data-testid="test-count" />
          </div>
          <div>
            <Label className="text-xs">Nombre de patients</Label>
            <Input type="number" min="1" max="1000" value={cfg.patients} onChange={(e) => set("patients", e.target.value)} className="mt-1" data-testid="test-patients" />
            <span className="block text-[11px] text-muted-foreground mt-1">Les patients sont réutilisés si moins nombreux que les courses.</span>
          </div>
        </div>

        <div>
          <Label className="text-xs">Région / bassin <span className="text-muted-foreground">(ignorée si une ville est saisie ci-dessous)</span></Label>
          <select value={cfg.region} onChange={(e) => set("region", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="test-region">
            {regions.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-md border border-dashed border-border p-3">
          <div className="sm:col-span-2">
            <Label className="text-xs">Ville précise <span className="text-muted-foreground">(départs autour + établissements réels)</span></Label>
            <Input value={cfg.city} onChange={(e) => set("city", e.target.value)} placeholder="Ex : Nevers, Mâcon, Lyon…" className="mt-1" data-testid="test-city" />
          </div>
          <div>
            <Label className="text-xs">Rayon (km)</Label>
            <Input type="number" min="1" max="60" value={cfg.radius_km} onChange={(e) => set("radius_km", e.target.value)} className="mt-1" data-testid="test-radius" />
          </div>
          <div className="sm:col-span-3">
            <Button type="button" variant="secondary" size="sm" onClick={journeeType} data-testid="test-preset-journee">
              ⚡ Journée type (120 courses, 06:00–19:00)
            </Button>
          </div>
        </div>

        <div>
          <Label className="text-xs">Répartition des types (%) — total {totalPct}%{totalPct !== 100 ? " (normalisé automatiquement)" : ""}</Label>
          <div className="grid grid-cols-3 gap-3 mt-1">
            <div><span className="text-[11px] text-muted-foreground">Sanitaire</span><Input type="number" min="0" max="100" value={cfg.pct_sanitaire} onChange={(e) => set("pct_sanitaire", e.target.value)} data-testid="test-pct-sanitaire" /></div>
            <div><span className="text-[11px] text-muted-foreground">Privé (taxi)</span><Input type="number" min="0" max="100" value={cfg.pct_prive} onChange={(e) => set("pct_prive", e.target.value)} data-testid="test-pct-prive" /></div>
            <div><span className="text-[11px] text-muted-foreground">Tourisme</span><Input type="number" min="0" max="100" value={cfg.pct_tourisme} onChange={(e) => set("pct_tourisme", e.target.value)} data-testid="test-pct-tourisme" /></div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Date de début</Label>
            <Input type="date" value={cfg.date} onChange={(e) => set("date", e.target.value)} className="mt-1" data-testid="test-date" />
          </div>
          <div>
            <Label className="text-xs">Nombre de jours</Label>
            <Input type="number" min="1" max="14" value={cfg.days} onChange={(e) => set("days", e.target.value)} className="mt-1" data-testid="test-days" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">De</Label><Input type="time" value={cfg.time_start} onChange={(e) => set("time_start", e.target.value)} className="mt-1" data-testid="test-time-start" /></div>
            <div><Label className="text-xs">À</Label><Input type="time" value={cfg.time_end} onChange={(e) => set("time_end", e.target.value)} className="mt-1" data-testid="test-time-end" /></div>
          </div>
        </div>

        <div className="space-y-2 border-t border-border pt-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={!!cfg.cluster} onCheckedChange={(v) => set("cluster", !!v)} data-testid="test-cluster" />
            <span><span className="font-medium">Regrouper en grappes</span> — plusieurs patients vers le même établissement dans une fenêtre courte (pour tester la mutualisation).</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={!!cfg.auto_regul} onCheckedChange={(v) => set("auto_regul", !!v)} data-testid="test-auto-regul" />
            <span><span className="font-medium">Réguler automatiquement</span> après génération (affectation véhicules/chauffeurs).</span>
          </label>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button onClick={generate} disabled={busy} data-testid="test-generate">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
            Générer
          </Button>
          <Button variant="outline" onClick={clearAll} disabled={busy} data-testid="test-clear">
            <Trash2 className="h-4 w-4 mr-2" /> Tout supprimer (données de test)
          </Button>
          {last && (
            <Button variant="secondary" onClick={() => navigate("/jeregul")} data-testid="test-goto-jeregul">
              Voir dans Jeregul →
            </Button>
          )}
        </div>

        {last && (
          <div className="border border-border rounded-md p-4 text-sm bg-muted/40" data-testid="test-result">
            <p className="font-medium">{last.rides} courses · {last.patients} patients — {last.region}</p>
            <p className="text-muted-foreground text-xs mt-1">Dates : {last.dates.join(", ")}</p>
            {Object.keys(last.regul || {}).length > 0 && (
              <ul className="text-xs text-muted-foreground mt-2 space-y-0.5">
                {Object.entries(last.regul).map(([d, r]) => (
                  <li key={d}>{d} : {r.assigned} affectées · {r.grouped} mutualisées · {r.surcharge} en surcharge · {r.vehicles_used} véhicules</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
